import { Chip, Typography } from "@mui/material"
import SearchIcon from "@mui/icons-material/Search"
import { DataGridMui } from "src/shared/container/data-grid-mui"
import Stack from "src/shared/components/stack"
// import { useNavigate } from "react-router-dom"
import getReturnOrderListColumns from "./getReturnOrderListColumns"
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined"
import {
  LogicalOperator,
  FilterCondition,
  useGetReturnedOrdersQuery,
  FilterOperator,
  SortDirection
} from "src/graphql/quark/quark.type"
import { useMemo, useState } from "react"
import { useTypedSelector } from "src/shared/store/store"
import { Conditional } from "src/shared/components"
import { usePermission } from "src/shared/hooks/usePermission"
import DebouncedInput from "src/shared/components/debounced-Input"
import { useSearchParams } from "react-router-dom"
import ReturnOrderDetailModal from "./ReturnOrderDetailModal"
import { useTranslation } from "react-i18next"

const ReturnOrderList = () => {
  const { t } = useTranslation("sales-orders")
  // const navigate = useNavigate()
  const { hasPermission } = usePermission()
  const [searchParams, setSearchParams] = useSearchParams()
  const columns = getReturnOrderListColumns({ onDetailClick: (id) => setSearchParams({ id }) })
  const currentWhLoc = useTypedSelector((state) => state.authentication.currentWhLoc)
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 20 })
  const [filters, setFilters] = useState<{ id: string; value: string }[]>([])
  const [sorting, setSorting] = useState<{ id?: null | string; desc?: null | boolean }>({
    id: "secondaryId",
    desc: true
  })
  const filterConditions: FilterCondition[] = useMemo(() => {
    if (!filters?.length) return []
    return filters.map(
      (val) =>
        ({
          operator: val.id === "secondaryId" ? FilterOperator.Equal : FilterOperator.Like,
          field: val.id,
          value: val.value
        }) as FilterCondition
    )
  }, [filters])

  const { data, previousData, loading, refetch } = useGetReturnedOrdersQuery({
    fetchPolicy: "network-only",
    variables: {
      limit: pagination.pageSize,
      page: pagination.pageIndex + 1,
      locationId: currentWhLoc?.id as string,
      filter: {
        conditions: filterConditions,
        logicalOperator: LogicalOperator.And
      },
      sort: sorting?.id
        ? [
            {
              direction: sorting?.desc ? SortDirection.Desc : SortDirection.Asc,
              field: sorting.id
            }
          ]
        : []
    }
  })
  const totalCount = previousData?.getReturnedOrders?.meta?.totalCount ?? data?.getReturnedOrders?.meta?.totalCount ?? 0
  return (
    <>
      <Stack height={"100%"} p={2}>
        {/* <Stack variant="rowCenterSpaceBetween"> */}
        <Stack mb={2}>
          <Typography variant="h3">{t("returnOrder.title")}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t("returnOrder.subtitle")}
          </Typography>
        </Stack>
        {/* </Stack> */}

        <DataGridMui
          id="return-order-list-table"
          columns={columns}
          data={data?.getReturnedOrders.data || []}
          enableColumnFilters={false}
          enableSorting={false}
          onPaginationChange={setPagination}
          paginationModel={pagination}
          isLoading={loading}
          onRefresh={refetch}
          onFilterChange={setFilters}
          sortingModel={sorting as any}
          onSortModelChange={setSorting}
          paginationMode="server"
          rowsPerPageOptions={[20, 50, 100]}
          sortingMode="server"
          filterMode="server"
          pageCount={totalCount ?? undefined}
          leftComponent={
            <DebouncedInput
              variant="rounded"
              value={(filters?.find((val) => val.id === "productName")?.value ?? "") as string}
              onChange={(val) => {
                if (val === undefined) return

                setFilters((prev) => {
                  const data = [...prev]
                  const nameIndex = data?.findIndex((prev) => prev.id === "productName")
                  if (nameIndex === -1) {
                    data.push({ id: "productName", value: val as string })
                    return data
                  } else {
                    if (!val) return data.filter((prev) => prev.id !== "productName")

                    data[nameIndex].value = val as string
                    return data
                  }
                })
              }}
              startAdornment={<SearchIcon />}
              placeholder={t("returnOrder.searchPlaceholder")}
              size="small"
            />
          }
          rightComponent={
            <Conditional condition={hasPermission("sales-orders:return-order:export")}>
              <Chip
                size="small"
                color="primary"
                variant="outlined"
                icon={<FileUploadOutlinedIcon fontSize="small" />}
                label={t("returnOrder.exportButton")}
              />
            </Conditional>
          }
        />
        <ReturnOrderDetailModal
          open={Boolean(searchParams.get("id"))}
          id={searchParams.get("id")}
          onClose={() => {
            const params = new URLSearchParams(searchParams)
            params.delete("id")
            setSearchParams(params)
          }}
        />
      </Stack>
    </>
  )
}

export default ReturnOrderList
